import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { TNPSC_UNIT3_QUESTIONS } from "../data/tnpscUnit3Questions";

// ─────────────────────────────────────────────────────────────────────────────
// TNPSC Code 554 — Unit III: Digital Logic and Computer Architecture.
// Content drafted from the official syllabus topic list; each concept has an
// explanation plus a pseudocode/diagram example. NOT YET human-reviewed by a
// subject-matter expert — treat as a first draft, verify before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Digital Logic", "Computer Organization", "ISA & Pipelining", "Memory Systems", "I/O & Parallel Architectures"];

const SECTION_COLORS = {
  "Digital Logic":                 "#2563eb",
  "Computer Organization":         "#16a34a",
  "ISA & Pipelining":              "#d97706",
  "Memory Systems":                "#dc2626",
  "I/O & Parallel Architectures":  "#7c3aed",
};

const CONCEPTS = [
  // ── Digital Logic ─────────────────────────────────────────────────────
  { section: "Digital Logic", title: "Boolean Algebra and Logic Gates",
    explanation: "Boolean algebra models digital logic using variables that are 0 or 1, combined via AND (·), OR (+), and NOT operations. Basic gates (AND, OR, NOT, NAND, NOR, XOR, XNOR) implement these operations in hardware. NAND and NOR are 'universal gates' — either one alone can implement any other gate, which is why real chips are often built almost entirely from just one of them.",
    example: `// De Morgan's Laws — heavily tested by TNPSC
NOT(A AND B) = (NOT A) OR (NOT B)
NOT(A OR B)  = (NOT A) AND (NOT B)

// XOR truth table
// A B | A XOR B
// 0 0 |   0
// 0 1 |   1
// 1 0 |   1
// 1 1 |   0` },
  { section: "Digital Logic", title: "Combinational Logic",
    explanation: "Combinational circuits produce outputs that depend ONLY on the current inputs, with no memory of past inputs — built purely from logic gates with no feedback loop. Classic examples: adders, multiplexers (MUX, select one of many inputs), demultiplexers, encoders, and decoders.",
    example: `// 2-to-1 MUX: output = select ? inputB : inputA
Y = (S' . A) + (S . B)

// Half adder: Sum = A XOR B, Carry = A AND B` },
  { section: "Digital Logic", title: "Sequential Logic",
    explanation: "Sequential circuits' outputs depend on both current inputs AND the circuit's past state (stored via memory elements like flip-flops), requiring a clock signal to synchronize state changes. Common building blocks: SR, D, JK, and T flip-flops; built from these are registers, counters, and finite state machines.",
    example: `// D flip-flop: output Q takes the value of D
// on the next active clock edge, and HOLDS it
// until the next edge — this "holding" is exactly
// what gives sequential circuits memory.` },

  // ── Computer Organization ────────────────────────────────────────────────
  { section: "Computer Organization", title: "Functional Units of a Digital Computer",
    explanation: "A digital computer's core functional units are: the CPU (Control Unit + ALU + registers), Memory (stores instructions and data), Input/Output devices, and the System Bus (address, data, and control lines connecting them). The Control Unit directs the fetch-decode-execute cycle; the ALU performs arithmetic/logic operations.",
    example: `// Fetch-Decode-Execute cycle
1. FETCH   — read instruction from memory at [PC]
2. DECODE  — determine opcode & operands
3. EXECUTE — ALU performs the operation
4. WRITEBACK — store result, PC = PC + instruction size` },
  { section: "Computer Organization", title: "Arithmetic operations: Addition and Subtraction",
    explanation: "Binary addition uses the same carry logic as decimal addition. Subtraction in computers is usually done via 2's complement: to compute A − B, add A to the 2's complement of B (invert all bits of B, then add 1). This lets the SAME adder hardware handle both addition and subtraction.",
    example: `// 2's complement subtraction: 5 - 3 using 4-bit numbers
  0101   (5)
+ 1101   (2's complement of 3: invert 0011->1100, +1->1101)
------
 10010   -> drop the overflow carry -> 0010 = 2  ✓` },
  { section: "Computer Organization", title: "Binary Multiplication",
    explanation: "Binary multiplication mirrors decimal long multiplication (shift-and-add), producing a result up to twice the width of the operands. Hardware implementations include the simple 'shift and add' algorithm and Booth's algorithm, which speeds things up by handling strings of 1s more efficiently and correctly handles signed numbers.",
    example: `   1011   (11)
 x 1101   (13)
 ------
   1011
  0000  (shift 1)
 1011   (shift 2)
1011    (shift 3)
 ------
10001111  (143)` },
  { section: "Computer Organization", title: "Binary Division",
    explanation: "Binary division follows restoring or non-restoring long-division algorithms: repeatedly compare the divisor against the current partial remainder, subtract if it fits (quotient bit = 1), otherwise shift (quotient bit = 0). Non-restoring division avoids the 'restore' step of restoring division, making it faster in hardware.",
    example: `// Restoring division (simplified):
// 1. Shift remainder left, bring down next dividend bit
// 2. Subtract divisor from remainder
// 3. If result is negative, RESTORE (add divisor back), bit = 0
// 4. Else keep the result, quotient bit = 1` },
  { section: "Computer Organization", title: "Floating Point Numbers",
    explanation: "IEEE 754 represents floating-point numbers as (sign, exponent, mantissa/fraction). Single precision uses 1+8+23 bits (32 total); double precision uses 1+11+52 bits (64 total). The exponent is stored with a BIAS (127 for single, 1023 for double) so it can represent negative exponents using only unsigned bits.",
    example: `// IEEE 754 single precision layout (32 bits)
[ S(1) | Exponent(8) | Mantissa(23) ]
value = (-1)^S * 1.Mantissa * 2^(Exponent - 127)` },

  // ── ISA & Pipelining ────────────────────────────────────────────────────
  { section: "ISA & Pipelining", title: "Addressing Modes",
    explanation: "Addressing modes specify how an instruction's operand is located. Immediate: the operand is embedded in the instruction itself. Direct: the instruction holds the operand's memory address. Indirect: the instruction holds the address of a location that holds the operand's address. Register/Register-indirect, and Indexed (base + offset) are also common.",
    example: `MOV R1, #5      ; immediate — operand 5 is right in the instruction
MOV R1, [1000]  ; direct    — operand is AT memory address 1000
MOV R1, [[1000]]; indirect  — address 1000 holds ANOTHER address
MOV R1, [R2+4]  ; indexed   — address = R2's value + offset 4` },
  { section: "ISA & Pipelining", title: "Instruction Set Architecture",
    explanation: "An Instruction Set Architecture (ISA) defines the interface between hardware and software: the instructions a CPU can execute, registers, data types, addressing modes, and memory model. It's the contract that lets the same compiled binary run on any CPU implementing that ISA, regardless of the underlying microarchitecture.",
    example: `// Instruction formats commonly tested:
// R-type (register-register), I-type (immediate),
// J-type (jump) — e.g. in MIPS-style ISAs` },
  { section: "ISA & Pipelining", title: "RISC and CISC Architectures",
    explanation: "RISC (Reduced Instruction Set Computer) uses a small set of simple, fixed-length instructions, each typically executing in one clock cycle, relying on compilers to combine them for complex tasks (e.g. ARM, MIPS). CISC (Complex Instruction Set Computer) uses larger, variable-length instructions that can do more work per instruction (e.g. x86). RISC favors pipelining; CISC favors code density.",
    example: `// RISC: load-store architecture — only LOAD/STORE touch memory,
//        all arithmetic happens on registers
// CISC: an instruction like x86's ADD can operate DIRECTLY on
//        a memory operand in a single instruction` },
  { section: "ISA & Pipelining", title: "CPU Performance Metrics",
    explanation: "CPU time = Instruction Count × CPI (Cycles Per Instruction) × Clock Cycle Time. Improving performance means reducing any of these three factors — but they interact: e.g. a RISC ISA lowers CPI but may raise instruction count. MIPS (millions of instructions per second) and FLOPS are also common but can be misleading metrics across different ISAs.",
    example: `CPU Time = Instruction Count × CPI × Clock Cycle Time
         = Instruction Count × CPI / Clock Rate

// Example: 10^9 instructions, CPI=2, Clock=2GHz
// CPU Time = 10^9 * 2 / (2*10^9) = 1 second` },
  { section: "ISA & Pipelining", title: "Data path and Control",
    explanation: "The datapath is the hardware that performs operations on data — registers, ALU, buses, and multiplexers wired together. The control unit generates the signals that direct the datapath's components each cycle (which registers to read/write, what the ALU should do, which mux inputs to select) — implemented either as hardwired logic or microprogrammed control.",
    example: `// Simplified single-cycle datapath signal flow:
PC -> Instruction Memory -> Decode
Register File (read) -> ALU -> Data Memory / Register File (write)
// Control unit sets: RegWrite, MemRead, MemWrite, ALUOp, etc.` },
  { section: "ISA & Pipelining", title: "Hazards: Structural, Data and Control Hazards",
    explanation: "In a pipelined CPU, a STRUCTURAL hazard occurs when two instructions need the same hardware resource simultaneously. A DATA hazard occurs when an instruction depends on the result of a not-yet-completed prior instruction (fixed via forwarding/stalling). A CONTROL hazard occurs from branches, where the next instruction to fetch isn't known until the branch resolves.",
    example: `// Data hazard example:
ADD R1, R2, R3   ; R1 = R2 + R3
SUB R4, R1, R5   ; needs R1 — but R1 isn't written back yet!
// Fixed by: forwarding (bypass the result directly) or stalling` },
  { section: "ISA & Pipelining", title: "Dynamic Scheduling",
    explanation: "Dynamic scheduling lets the CPU hardware reorder instruction execution at RUN TIME to avoid stalling on hazards, rather than relying purely on the compiler's static instruction order. Tomasulo's algorithm is the classic implementation, using reservation stations and register renaming to eliminate WAR/WAW hazards and allow out-of-order execution.",
    example: `// Tomasulo's algorithm core ideas:
// - Reservation stations buffer operands + waiting instructions
// - Register renaming removes false (WAR/WAW) dependencies
// - Common Data Bus broadcasts results to all waiting stations` },
  { section: "ISA & Pipelining", title: "Speculation",
    explanation: "Speculative execution predicts the outcome of an uncertain event (most commonly a branch direction) and executes instructions along the predicted path BEFORE it's confirmed, to avoid stalling the pipeline. If the prediction is wrong, the speculatively-executed results must be discarded/rolled back ('squashed') and the correct path fetched instead.",
    example: `// Branch prediction + speculation:
if (predicted taken) {
    speculatively fetch & execute from the branch target
} else {
    speculatively fetch & execute the fall-through instructions
}
// on misprediction: flush the pipeline, restart from correct path` },
  { section: "ISA & Pipelining", title: "ILP and Thread Level Parallelism",
    explanation: "Instruction-Level Parallelism (ILP) extracts parallelism WITHIN a single instruction stream, exploited via pipelining, superscalar issue (multiple instructions per cycle), and out-of-order execution. Thread-Level Parallelism (TLP) runs multiple independent threads/processes simultaneously, exploited via multicore/multithreaded processors — useful when ILP within one thread is limited.",
    example: `// ILP: one thread, multiple instructions issued/executed per cycle
//      (superscalar, VLIW, out-of-order execution)
// TLP: multiple threads running in parallel across cores
//      (multicore CPUs, simultaneous multithreading / SMT)` },

  // ── Memory Systems ──────────────────────────────────────────────────────
  { section: "Memory Systems", title: "Memory Hierarchy",
    explanation: "The memory hierarchy exploits a trade-off: faster memory is more expensive and smaller, slower memory is cheaper and larger. Typical order (fastest/smallest to slowest/largest): registers → L1 cache → L2 cache → L3 cache → main memory (RAM) → disk/SSD. It works because of locality of reference, letting a small fast cache serve most accesses.",
    example: `// Typical access latencies (illustrative)
// Registers  ~ 1 cycle          L3 cache  ~ 30-40 cycles
// L1 cache   ~ a few cycles     RAM       ~ 100+ cycles
// L2 cache   ~ ~10 cycles       SSD/Disk  ~ thousands+ cycles` },
  { section: "Memory Systems", title: "Cache Memories",
    explanation: "A cache stores recently/frequently used data close to the CPU to exploit locality of reference. Mapping strategies: direct-mapped (each memory block maps to exactly ONE cache line), fully associative (a block can go in ANY line), and set-associative (a middle ground — a block maps to one of N lines in a set).",
    example: `// Direct-mapped cache index calculation
cache_line = (memory_address / block_size) % num_cache_lines

// Cache miss types: Compulsory (first access),
// Capacity (cache too small), Conflict (mapping collision)` },
  { section: "Memory Systems", title: "Virtual Memory",
    explanation: "Virtual memory gives each process the illusion of a large, private, contiguous address space, backed by physical RAM and disk. A page table maps virtual pages to physical frames; a page fault occurs when a needed page isn't in RAM and must be loaded from disk. The Translation Lookaside Buffer (TLB) caches recent virtual-to-physical translations to avoid slow page-table walks.",
    example: `// Virtual address -> Physical address translation
Virtual Address = [ Page Number | Page Offset ]
Physical Address = PageTable[PageNumber].frame + PageOffset
// TLB hit -> skip the page table walk entirely (fast path)` },
  { section: "Memory Systems", title: "Associative memories",
    explanation: "An associative (content-addressable) memory is searched by CONTENT rather than by address — you supply a data pattern, and it returns the matching location(s) in parallel across all entries, in constant time regardless of memory size. It's more expensive in hardware than regular RAM but is used where lookups need to be extremely fast, such as fully-associative caches and TLBs.",
    example: `// Given a search key, an associative memory compares it
// against EVERY stored entry SIMULTANEOUSLY (in parallel
// hardware), returning matching entries in constant time —
// unlike RAM, which requires a known address to look up.` },

  // ── I/O & Parallel Architectures ────────────────────────────────────────
  { section: "I/O & Parallel Architectures", title: "Accessing I/O devices, Interrupts",
    explanation: "CPUs access I/O devices via memory-mapped I/O (device registers appear at specific memory addresses) or isolated/port-mapped I/O (separate address space with dedicated instructions). Polling repeatedly checks a device's status flag, wasting CPU cycles; interrupts let a device signal the CPU asynchronously when it needs attention.",
    example: `// Polling: CPU repeatedly checks in a loop — wastes cycles
while (device.status != READY) { /* busy-wait */ }

// Interrupt-driven: CPU does other work; device raises an
// interrupt signal -> CPU jumps to an Interrupt Service Routine` },
  { section: "I/O & Parallel Architectures", title: "Direct Memory Access",
    explanation: "DMA lets peripheral devices transfer data directly to/from main memory WITHOUT the CPU shuttling every byte through registers, freeing the CPU to do other work during the transfer. A DMA controller takes over the system bus for the duration of the transfer (cycle stealing) and raises an interrupt when the transfer completes.",
    example: `// Without DMA: CPU reads each byte from device, writes to memory
//              — CPU busy for the entire transfer
// With DMA:    DMA controller moves the data; CPU is free meanwhile
//              DMA raises an interrupt when done` },
  { section: "I/O & Parallel Architectures", title: "Multicore Architectures",
    explanation: "A multicore processor integrates multiple independent CPU cores on a single chip, each capable of executing its own instruction stream — enabling true thread-level parallelism. Cores may share some cache levels (e.g. shared L3) while having private L1/L2, raising cache coherence concerns whenever multiple cores cache the same memory location.",
    example: `// Multicore layout (typical)
Core0[L1,L2] --\\
Core1[L1,L2] ---+--- Shared L3 Cache --- Main Memory
Core2[L1,L2] --/` },
  { section: "I/O & Parallel Architectures", title: "OpenMP",
    explanation: "OpenMP is a shared-memory parallel programming API using compiler directives (pragmas in C/C++) to parallelize loops and code regions across threads on a single multicore machine, without needing explicit thread management. It's suited to fine-grained parallelism where all threads can directly access the same memory.",
    example: `#pragma omp parallel for
for (int i = 0; i < n; i++) {
    a[i] = b[i] + c[i];   // each iteration can run on a different thread
}` },
  { section: "I/O & Parallel Architectures", title: "MPI",
    explanation: "MPI (Message Passing Interface) is a standard for parallel programming across DISTRIBUTED-memory systems (e.g. a cluster of separate machines), where processes communicate explicitly via message passing (send/receive) rather than sharing memory directly. It's the standard choice for large-scale, multi-node supercomputing.",
    example: `// MPI: explicit message passing between processes
if (rank == 0) {
    MPI_Send(data, count, MPI_INT, 1, tag, MPI_COMM_WORLD);
} else if (rank == 1) {
    MPI_Recv(data, count, MPI_INT, 0, tag, MPI_COMM_WORLD, &status);
}` },
  { section: "I/O & Parallel Architectures", title: "Cache coherence policies",
    explanation: "Cache coherence ensures all cores see a CONSISTENT view of memory when multiple cores cache the same location. The MESI protocol (Modified, Exclusive, Shared, Invalid) is the classic solution: a cache line's state tracks whether it's uniquely modified, exclusively/shared clean, or invalid, and cores use bus snooping to invalidate/update other caches' copies when one core writes.",
    example: `// MESI states for a cache line:
// M(odified) — dirty, only this cache has it
// E(xclusive) — clean, only this cache has it
// S(hared)    — clean, possibly cached in other cores too
// I(nvalid)   — not valid, must reload from memory/another cache` },
  { section: "I/O & Parallel Architectures", title: "GPU architectures and programming",
    explanation: "GPUs use a SIMT (Single Instruction, Multiple Threads) architecture with thousands of lightweight cores organized into groups (warps/wavefronts) that execute the SAME instruction across many data elements in lockstep, unlike a CPU's few powerful, independent cores. This makes GPUs excel at data-parallel workloads but perform poorly on code with heavy branching (warp divergence).",
    example: `// CUDA kernel — runs on thousands of GPU threads in parallel
__global__ void addVectors(int *a, int *b, int *c, int n) {
    int i = blockIdx.x * blockDim.x + threadIdx.x;
    if (i < n) c[i] = a[i] + b[i];   // each thread handles ONE element
}` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
};

function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function TnpscUnit3Page() {
  const [mode, setMode] = useState("guide");
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  const filtered = useMemo(() => (
    activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection)
  ), [activeSection]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? TNPSC_UNIT3_QUESTIONS : TNPSC_UNIT3_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => { setMode(m); setScore({ correct: 0, answered: 0 }); };
  const handleAnswer = (isCorrect) => setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));

  return (
    <div style={S.page}>
      <Link to="/tnpsc" style={S.back}>← Back to TNPSC 554 Menu</Link>
      {" · "}
      <Link to="/tnpsc-unit2" style={S.back}>← Unit II</Link>
      {" · "}
      <Link to="/tnpsc-unit4" style={S.back}>Unit IV →</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🎓 TNPSC 554 — Unit III</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Digital Logic and Computer Architecture</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          20 questions in the exam · {CONCEPTS.length} concepts explained · {TNPSC_UNIT3_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify before exam use
        </div>
      </div>

      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({TNPSC_UNIT3_QUESTIONS.length})</button>
      </div>

      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a href={`#concept-${i}`} style={{ color:"#e2e8f0", textDecoration:"none" }}>{c.title}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filtered.map((c, i) => (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / SCRIPT</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
      ))}
      </>
      )}
    </div>
  );
}
